import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SessionUser } from '../../shared/types';
import { api, post } from '../lib/api';
import { useConfig, useMe } from '../lib/session';
import { BrandLogo, useTitle } from '../components/AppShell';
import { errorMessage } from '../components/toast';
import { Emphasis, Field, Notice, Spinner } from '../components/ui';

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function AuthLayout({ children }: { children: ReactNode }) {
  const { data: config } = useConfig();
  const b = config?.branding;
  return (
    <div className="auth">
      <section className="auth-hero">
        <BrandLogo dark />
        <div className="stack" style={{ gap: 20 }}>
          {b?.loginHeadline && (
            <h1>
              <Emphasis text={b.loginHeadline} />
            </h1>
          )}
          {b?.loginText && <p>{b.loginText}</p>}
        </div>
        <span className="small" style={{ color: 'rgba(255,255,255,.5)' }}>
          {b?.appName}
          {b?.showPoweredBy && b.appName !== 'ZeigBanner' ? ' · läuft mit ZeigBanner' : ''}
        </span>
      </section>
      <section className="auth-panel">{children}</section>
    </div>
  );
}

function SsoButtons({ next, invite }: { next?: string; invite?: string }) {
  const { data: config } = useConfig();
  if (!config?.providers.length) return null;
  return (
    <div className="stack" style={{ gap: 10 }}>
      {config.providers.map((p) => {
        const params = new URLSearchParams();
        if (next) params.set('next', next);
        if (invite) params.set('invite', invite);
        return (
          <a key={p.id} className="btn btn-sso btn-block" href={`/api/auth/oidc/${p.id}/start?${params}`}>
            {p.id === 'microsoft' && <MicrosoftLogo />}
            {invite ? `Mit ${p.name} fortfahren` : `Mit ${p.name} anmelden`}
          </a>
        );
      })}
    </div>
  );
}

function useLoginSuccess() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return (user: SessionUser, next = '/') => {
    queryClient.setQueryData(['me'], user);
    navigate(next.startsWith('/') && !next.startsWith('//') ? next : '/', { replace: true });
  };
}

export function LoginPage() {
  useTitle('Anmelden');
  const [params] = useSearchParams();
  const next = params.get('weiter') ?? '/';
  const { data: config } = useConfig();
  const { data: me } = useMe();
  const done = useLoginSuccess();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(params.get('fehler'));
  const [busy, setBusy] = useState(false);

  if (me) return <Navigate to={next} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user } = await post<{ user: SessionUser }>('/api/auth/login', { email, password });
      done(user, next);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <div className="auth-box">
        <div className="stack" style={{ gap: 6 }}>
          <span className="eyebrow">Willkommen</span>
          <h2>Anmelden</h2>
        </div>
        {error && <Notice kind="error">{error}</Notice>}
        <SsoButtons next={next} />
        {config?.passwordLogin && (
          <>
            {!!config.providers.length && <div className="or">oder mit E-Mail</div>}
            <form className="stack" style={{ gap: 14 }} onSubmit={submit}>
              <Field label="E-Mail">
                {(id) => (
                  <input
                    id={id}
                    className="input"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Passwort">
                {(id) => (
                  <input
                    id={id}
                    className="input"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                )}
              </Field>
              <button className="btn btn-primary btn-block btn-arrow" disabled={busy} style={{ height: 46 }}>
                Anmelden
              </button>
            </form>
            {config.mailEnabled ? (
              <Link className="small text-link" to="/passwort-vergessen">
                Passwort vergessen?
              </Link>
            ) : (
              <p className="small muted">Passwort vergessen? Dein Admin schickt dir einen neuen Link.</p>
            )}
          </>
        )}
        <div className="auth-foot">
          {config?.branding.imprintUrl && <a href={config.branding.imprintUrl}>Impressum</a>}
          {config?.branding.privacyUrl && <a href={config.branding.privacyUrl}>Datenschutz</a>}
          {config?.branding.supportEmail && <a href={`mailto:${config.branding.supportEmail}`}>Kontakt</a>}
        </div>
      </div>
    </AuthLayout>
  );
}

function PasswordForm({
  submitLabel,
  askName,
  initialName = '',
  onSubmit,
}: {
  submitLabel: string;
  askName?: boolean;
  initialName?: string;
  onSubmit: (values: { name: string; password: string }) => Promise<void>;
}) {
  const [name, setName] = useState(initialName);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="stack"
      style={{ gap: 14 }}
      onSubmit={async (e) => {
        e.preventDefault();
        if (password !== repeat) {
          setError('Die Passwörter stimmen nicht überein.');
          return;
        }
        setBusy(true);
        setError(null);
        try {
          await onSubmit({ name, password });
        } catch (err) {
          setError(errorMessage(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      {error && <Notice kind="error">{error}</Notice>}
      {askName && (
        <Field label="Dein Name">
          {(id) => (
            <input
              id={id}
              className="input"
              required
              value={name}
              autoComplete="name"
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
      )}
      <Field
        label="Neues Passwort"
        hint="Mindestens 10 Zeichen. Ein Satz aus mehreren Wörtern ist sicher und leicht zu merken."
      >
        {(id) => (
          <input
            id={id}
            className="input"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
      </Field>
      <Field label="Passwort wiederholen">
        {(id) => (
          <input
            id={id}
            className="input"
            type="password"
            autoComplete="new-password"
            required
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
          />
        )}
      </Field>
      <button className="btn btn-primary btn-block btn-arrow" disabled={busy} style={{ height: 46 }}>
        {submitLabel}
      </button>
    </form>
  );
}

export function InvitePage() {
  useTitle('Einladung');
  const { token = '' } = useParams();
  const done = useLoginSuccess();
  const { data: config } = useConfig();
  const info = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<{ email: string; name: string }>(`/api/auth/invite/${token}`),
    retry: false,
  });
  return (
    <AuthLayout>
      <div className="auth-box">
        <div className="stack" style={{ gap: 6 }}>
          <span className="eyebrow">Einladung</span>
          <h2>Konto einrichten</h2>
        </div>
        {info.isLoading && <Spinner />}
        {info.error && <Notice kind="error">{errorMessage(info.error)}</Notice>}
        {info.data && (
          <>
            <p className="muted">
              Für <strong style={{ color: 'var(--ink)' }}>{info.data.email}</strong>. Lege ein Passwort fest
              {config?.providers.length ? ' oder melde dich direkt mit deinem Firmenkonto an.' : '.'}
            </p>
            <SsoButtons invite={token} />
            {!!config?.providers.length && <div className="or">oder mit Passwort</div>}
            <PasswordForm
              askName
              initialName={info.data.name}
              submitLabel="Konto einrichten"
              onSubmit={async ({ name, password }) => {
                const { user } = await post<{ user: SessionUser }>(`/api/auth/invite/${token}`, {
                  name,
                  password,
                });
                done(user);
              }}
            />
          </>
        )}
      </div>
    </AuthLayout>
  );
}

export function ResetPage() {
  useTitle('Neues Passwort');
  const { token = '' } = useParams();
  const done = useLoginSuccess();
  const info = useQuery({
    queryKey: ['reset', token],
    queryFn: () => api<{ email: string }>(`/api/auth/reset/${token}`),
    retry: false,
  });
  return (
    <AuthLayout>
      <div className="auth-box">
        <div className="stack" style={{ gap: 6 }}>
          <span className="eyebrow">Passwort</span>
          <h2>Neues Passwort festlegen</h2>
        </div>
        {info.isLoading && <Spinner />}
        {info.error && <Notice kind="error">{errorMessage(info.error)}</Notice>}
        {info.data && (
          <>
            <p className="muted">
              Für {info.data.email}. Danach bist du direkt angemeldet; andere Sitzungen werden beendet.
            </p>
            <PasswordForm
              submitLabel="Passwort speichern"
              onSubmit={async ({ password }) => {
                const { user } = await post<{ user: SessionUser }>(`/api/auth/reset/${token}`, { password });
                done(user);
              }}
            />
          </>
        )}
      </div>
    </AuthLayout>
  );
}

export function ForgotPage() {
  useTitle('Passwort vergessen');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <AuthLayout>
      <div className="auth-box">
        <div className="stack" style={{ gap: 6 }}>
          <span className="eyebrow">Passwort</span>
          <h2>Passwort vergessen</h2>
        </div>
        {sent ? (
          <Notice kind="ok">
            Wenn es ein Konto mit dieser Adresse gibt, ist jetzt eine E-Mail mit einem Link unterwegs.
          </Notice>
        ) : (
          <form
            className="stack"
            style={{ gap: 14 }}
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await post('/api/auth/forgot', { email });
                setSent(true);
              } catch (err) {
                setError(errorMessage(err));
              }
            }}
          >
            {error && <Notice kind="error">{error}</Notice>}
            <Field label="E-Mail">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>
            <button className="btn btn-primary btn-block btn-arrow" style={{ height: 46 }}>
              Link anfordern
            </button>
          </form>
        )}
        <Link className="small text-link" to="/anmelden">
          Zurück zur Anmeldung
        </Link>
      </div>
    </AuthLayout>
  );
}
